import React, { useMemo, useState } from 'react';
import { ICON } from '../../../icons/iconIds';
import IconText from '../../icons/IconText';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Scatter,
  ComposedChart
} from 'recharts';
import { 
  transformStandardsBiasMonthForChart,
  getUniqueCRMCodesFromDrift,
  COLORS
} from '../../../utils/qaqc/chartDataTransform';
import { formatChartAxisDate } from '../../../utils/qaqc/dateFilters';
import { trendMeanZBounds, clamp } from '../../../utils/qaqc/chartOutliers';

const StandardsTrendChart = ({ 
  data,
  selectedElement = 'all',
  selectedCRM = 'all',
  onElementChange,
  onCRMChange,
  height = 400
}) => {
  const [showMedian, setShowMedian] = useState(false);
  
  // Transform data for chart
  const chartData = useMemo(() => {
    return transformStandardsBiasMonthForChart(
      data,
      selectedElement === 'all' ? null : selectedElement,
      selectedCRM
    );
  }, [data, selectedElement, selectedCRM]);
  
  // Group data by series (element or CRM)
  const seriesData = useMemo(() => {
    if (chartData.length === 0) return {};
    
    const grouped = {};
    chartData.forEach(item => {
      const seriesKey = selectedElement === 'all' ? item.element : item.crmCode;
      if (!grouped[seriesKey]) {
        grouped[seriesKey] = [];
      }
      grouped[seriesKey].push(item);
    });
    
    return grouped;
  }, [chartData, selectedElement]);
  
  // Prepare data for recharts (merge all series by month)
  const chartReadyData = useMemo(() => {
    const monthMap = {};
    
    Object.entries(seriesData).forEach(([seriesKey, items]) => {
      items.forEach(item => {
        if (!monthMap[item.month]) {
          monthMap[item.month] = { month: item.month };
        }
        monthMap[item.month][`${seriesKey}_mean`] = showMedian ? item.medianZ : item.meanZ;
        monthMap[item.month][`${seriesKey}_size`] = Math.sqrt(item.nCrm) * 2; // Point size based on n_crm
        monthMap[item.month][`${seriesKey}_nCrm`] = item.nCrm;
      });
    });
    
    return Object.values(monthMap).sort((a, b) => a.month.localeCompare(b.month));
  }, [seriesData, showMedian]);

  const { trendPlotData, trendYDomain } = useMemo(() => {
    const keys = Object.keys(seriesData);
    if (chartReadyData.length === 0 || keys.length === 0) {
      return { trendPlotData: chartReadyData, yDomain: [-0.75, 0.75] };
    }
    const allVals = [];
    chartReadyData.forEach((row) => {
      keys.forEach((k) => {
        const v = row[`${k}_mean`];
        if (typeof v === 'number' && !Number.isNaN(v)) allVals.push(v);
      });
    });
    let { lo, hi } = trendMeanZBounds(allVals);
    const clampLo = lo;
    const clampHi = hi;
    const pad = Math.max((hi - lo) * 0.12, 0.04);
    const yDomain = [clampLo - pad, clampHi + pad];
    const trendPlotData = chartReadyData.map((row) => {
      const out = { ...row };
      keys.forEach((k) => {
        const v = row[`${k}_mean`];
        if (typeof v !== 'number' || Number.isNaN(v)) return;
        const plot = clamp(v, clampLo, clampHi);
        out[`${k}_meanPlot`] = plot;
        if (Math.abs(v - plot) > 1e-9) {
          out[`${k}_meanClip`] = v.toFixed(2);
        }
      });
      return out;
    });
    return { trendPlotData, yDomain };
  }, [chartReadyData, seriesData]);
  
  // Get unique elements and CRMs for filters
  const { elements, crmCodes } = useMemo(() => {
    const uniqueElements = new Set();
    const uniqueCRMs = new Set();
    
    data.forEach(item => {
      if (item.element) uniqueElements.add(item.element);
      if (item.crm_code) uniqueCRMs.add(item.crm_code);
    });
    
    return {
      elements: Array.from(uniqueElements).sort(),
      crmCodes: Array.from(uniqueCRMs).sort()
    };
  }, [data]);
  
  // Color palette for series
  const seriesColors = ['#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981'];
  
  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length > 0) {
      return (
        <div style={{
          background: '#1f2937',
          border: '1px solid #374151',
          borderRadius: '6px',
          padding: '12px',
          color: '#f9fafb',
          fontSize: '0.875rem',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#06b6d4' }}>
            {label}
          </div>
          <div style={{ borderTop: '1px solid #374151', paddingTop: '8px' }}>
            {payload.map((entry, index) => {
              const seriesKey = String(entry.dataKey || '').replace('_meanPlot', '').replace('_mean', '');
              const nCrm = entry.payload[`${seriesKey}_nCrm`];
              const raw = entry.payload[`${seriesKey}_mean`];
              const displayVal =
                typeof raw === 'number' && !Number.isNaN(raw) ? raw : entry.value;
              
              return (
                <div key={index} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  gap: '20px', 
                  marginBottom: '4px',
                  alignItems: 'center'
                }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ 
                      width: '12px', 
                      height: '3px', 
                      background: entry.color,
                      display: 'inline-block'
                    }} />
                    {seriesKey}:
                  </span>
                  <span style={{ fontWeight: 'bold' }}>
                    {typeof displayVal === 'number' ? displayVal.toFixed(3) : displayVal}
                    <span style={{ fontSize: '0.75rem', color: '#9ca3af', marginLeft: '0.25rem' }}>
                      (n={nCrm})
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    return null;
  };

  const TrendDot = ({ cx, cy, payload, seriesKey, stroke }) => {
    if (cx == null || cy == null || !payload) return null;
    const clip = payload[`${seriesKey}_meanClip`];
    const v = payload[`${seriesKey}_mean`];
    const plot = payload[`${seriesKey}_meanPlot`];
    const high =
      clip && typeof v === 'number' && typeof plot === 'number' && v > plot;
    return (
      <g>
        <circle cx={cx} cy={cy} r={4} fill={stroke} stroke="#111827" strokeWidth={1} />
        {clip && (
          <text
            x={cx}
            y={high ? cy + 14 : cy - 10}
            fill="#e5e7eb"
            fontSize={9}
            textAnchor="middle"
          >
            {clip}
          </text>
        )}
      </g>
    );
  };
  
  return (
    <div className="standards-trend-chart">
      {/* Filter Controls */}
      <div style={{ 
        padding: '1rem',
        background: '#111827',
        border: '1px solid #374151',
        borderRadius: '8px',
        marginBottom: '1rem'
      }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Element Filter */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <label style={{ fontSize: '0.875rem', color: '#9ca3af' }}>Element:</label>
            <select
              value={selectedElement}
              onChange={(e) => onElementChange && onElementChange(e.target.value)}
              style={{
                padding: '0.25rem 0.75rem',
                background: '#374151',
                color: '#f9fafb',
                border: '1px solid #4b5563',
                borderRadius: '4px',
                fontSize: '0.875rem'
              }}
            >
              <option value="all">All Elements</option>
              {elements.map(element => (
                <option key={element} value={element}>{element}</option>
              ))}
            </select>
          </div>
          
          {/* CRM Filter */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <label style={{ fontSize: '0.875rem', color: '#9ca3af' }}>CRM:</label>
            <select
              value={selectedCRM}
              onChange={(e) => onCRMChange && onCRMChange(e.target.value)}
              style={{
                padding: '0.25rem 0.75rem',
                background: '#374151',
                color: '#f9fafb',
                border: '1px solid #4b5563',
                borderRadius: '4px',
                fontSize: '0.875rem'
              }}
            >
              <option value="all">All CRMs</option>
              {crmCodes.map(code => (
                <option key={code} value={code}>{code}</option>
              ))}
            </select>
          </div>
          
          {/* Mean/Median Toggle */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <label style={{ fontSize: '0.875rem', color: '#9ca3af' }}>Metric:</label>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              <button
                onClick={() => setShowMedian(false)}
                style={{
                  padding: '0.25rem 0.75rem',
                  background: !showMedian ? '#06b6d4' : '#374151',
                  color: !showMedian ? '#0c4a6e' : '#f9fafb',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  fontWeight: !showMedian ? '600' : '400',
                  cursor: 'pointer'
                }}
              >
                Mean Z
              </button>
              <button
                onClick={() => setShowMedian(true)}
                style={{
                  padding: '0.25rem 0.75rem',
                  background: showMedian ? '#06b6d4' : '#374151',
                  color: showMedian ? '#0c4a6e' : '#f9fafb',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  fontWeight: showMedian ? '600' : '400',
                  cursor: 'pointer'
                }}
              >
                Median Z
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Chart */}
      {chartReadyData.length === 0 ? (
        <div style={{ 
          padding: '3rem',
          textAlign: 'center',
          background: '#111827',
          border: '1px solid #374151',
          borderRadius: '8px',
          color: '#9ca3af'
        }}>
          <p>No data available for the selected filters.</p>
          <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
            Try selecting a different element or CRM code.
          </p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <LineChart 
            data={trendPlotData} 
            margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} />
            
            <XAxis 
              dataKey="month"
              stroke={COLORS.text}
              tick={{ fill: COLORS.text, fontSize: 11 }}
              angle={-45}
              textAnchor="end"
              height={80}
              tickFormatter={formatChartAxisDate}
              label={{ 
                value: 'Month', 
                position: 'insideBottom', 
                offset: -5,
                style: { fill: COLORS.text, fontSize: 12 }
              }}
            />
            
            <YAxis 
              stroke={COLORS.text}
              tick={{ fill: COLORS.text, fontSize: 11 }}
              label={{ 
                value: showMedian ? 'Median Z-Score' : 'Mean Z-Score', 
                angle: -90, 
                position: 'insideLeft',
                style: { fill: COLORS.text, fontSize: 12 }
              }}
              domain={trendYDomain}
              allowDataOverflow
              tickFormatter={(v) =>
                typeof v === 'number' && !Number.isNaN(v) ? v.toFixed(3) : String(v)
              }
            />
            
            {/* Reference line at 0 */}
            <ReferenceLine y={0} stroke={COLORS.text} strokeDasharray="5 5" strokeWidth={1} />
            <ReferenceLine y={0.5} stroke={COLORS.warning} strokeDasharray="3 3" strokeWidth={1} strokeOpacity={0.3} />
            <ReferenceLine y={-0.5} stroke={COLORS.warning} strokeDasharray="3 3" strokeWidth={1} strokeOpacity={0.3} />
            
            <Tooltip content={<CustomTooltip />} />
            
            <Legend 
              wrapperStyle={{ paddingTop: '10px' }}
              iconType="line"
            />
            
            {/* Render line for each series */}
            {Object.keys(seriesData).map((seriesKey, index) => (
              <Line
                key={seriesKey}
                type="monotone"
                dataKey={`${seriesKey}_meanPlot`}
                stroke={seriesColors[index % seriesColors.length]}
                strokeWidth={2}
                dot={(dotProps) => (
                  <TrendDot
                    {...dotProps}
                    seriesKey={seriesKey}
                    stroke={seriesColors[index % seriesColors.length]}
                  />
                )}
                name={seriesKey}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
      
      {/* Helper text */}
      <div style={{ 
        marginTop: '1rem',
        padding: '0.75rem',
        background: '#111827',
        border: '1px solid #374151',
        borderRadius: '6px',
        fontSize: '0.875rem', 
        color: '#9ca3af', 
        textAlign: 'center'
      }}>
        <IconText icon={ICON.uiTip} iconSize={16} as="span">Look for step changes</IconText> or gradual drifts that indicate method/instrument/process changes over time
      </div>
    </div>
  );
};

export default StandardsTrendChart;
